import { eventTypes } from "lib/utils/constants";
import React, { createContext, useContext, useEffect, ReactNode, useCallback } from "react";
import { useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import { publishPostMessage } from "lib/utils/helpers";

export type ProductType = "EMPTY_CART" | "CART" | "BACKGROUND_CART" | "OTHER";

export interface GlobalState {
  tokenId?: string;
  cartPayload?: Record<string, any>;
  cartJSData?: Record<string, any>;
  productType: ProductType;
  bureauEventId?: string;
  segmentId?: string;
}

export interface TokenContextType {
  state: GlobalState;
  actions: {
    setTokenId: (data: { tokenId: string }) => void;
    setSegmentId: (data: { segmentId: string }) => void;
  };
}

// Initial state for merchant configuration
const GlobalConfigIntialState: GlobalState = {
  productType: "BACKGROUND_CART",
};

const TokenContext = createContext<TokenContextType>({
  state: GlobalConfigIntialState,
  actions: {
    setTokenId: () => { },
    setSegmentId: () => { },
  },
});

// Types for merchant state
export enum GlobalStateActionType {
  SET_TOKEN_ID = "SET_TOKEN_ID",
  SET_CART_JS_DATA = "SET_CART_JS_DATA",
  SET_BUREAU_EVENT_ID = "SET_BUREAU_EVENT_ID",
  SET_SEGMENT_ID = "SET_SEGMENT_ID",
}

// Actions type
type TokenAction =
  | {
    type: GlobalStateActionType.SET_TOKEN_ID;
    payload: {
      tokenId: string;
      productType?: ProductType;
    };
  }
  | {
    type: GlobalStateActionType.SET_CART_JS_DATA;
    payload: {
      cartPayload: Record<string, any>;
      productType: ProductType;
      cartJSData: Record<string, any>;
    };
  }
  | {
    type: GlobalStateActionType.SET_BUREAU_EVENT_ID;
    payload: {
      bureauEventId: string;
    };
  }
  | {
    type: GlobalStateActionType.SET_SEGMENT_ID;
    payload: {
      segmentId: string;
    };
  };

// Merchant reducer function
function merchantReducer(state: GlobalState, action: TokenAction): GlobalState {
  switch (action.type) {
    case GlobalStateActionType.SET_TOKEN_ID: {
      return {
        ...state,
        tokenId: action.payload?.tokenId,
        productType: action.payload?.productType ?? state.productType,
      };
    }
    case GlobalStateActionType.SET_CART_JS_DATA: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case GlobalStateActionType.SET_BUREAU_EVENT_ID: {
      return {
        ...state,
        bureauEventId: action.payload?.bureauEventId,
      };
    }
    case GlobalStateActionType.SET_SEGMENT_ID: {
      return {
        ...state,
        segmentId: action.payload?.segmentId,
      };
    }
    default:
      return state;
  }
}

export const useToken = () => {
  const context = useContext(TokenContext);
  if (context === undefined) {
    throw new Error("useToken must be used within a TokenProvider");
  }
  return context;
};

interface TokenProviderProps {
  children: ReactNode;
}

export const TokenProvider: React.FC<TokenProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(merchantReducer, GlobalConfigIntialState);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const tokenFromParams = searchParams.get("tokenId");
    const page = searchParams.get("page")?.toLowerCase();

    if (tokenFromParams) {
      dispatch({
        type: GlobalStateActionType.SET_TOKEN_ID,
        payload: {
          tokenId: tokenFromParams,
          productType: page === "cart" ? "BACKGROUND_CART" : "OTHER",
        },
      });
    }
  }, [searchParams]);

  const handleParentData = (event: any) => {
    if (event.data.type === eventTypes.GET_CART_DATA) {
      const { cartPayload, cartType, cartJSData } = event?.data?.payload?.initData;
      if (cartPayload) {
        dispatch({
          type: GlobalStateActionType.SET_CART_JS_DATA,
          payload: {
            cartPayload,
            productType: cartType,
            cartJSData,
          },
        });
        publishPostMessage(eventTypes.CLOSE_CART_DATA, {});
      }
    }

    if (event.data.type === eventTypes.FLO_SET_BUREAU_EVENT_ID) {
      dispatch({
        type: GlobalStateActionType.SET_BUREAU_EVENT_ID,
        payload: {
          bureauEventId: event?.data?.payload?.bureauEventId,
        },
      });
      publishPostMessage(eventTypes.CLOSE_BUREAU_EVENT_ID, {});
    }

    if (event.data.type === eventTypes.ACTIVATE_CHECKOUT) {
      const tokenId = event?.data?.payload?.tokenId;
      const checkoutId = event?.data?.payload?.checkoutId;
      const fromCartToCheckout = event?.data?.payload?.fromCartToCheckout
      const auth = event?.data?.payload?.auth
      const test_tag = event?.data?.payload?.test_tag
      if (!tokenId) return;

      // Redirecting out of a cookie blocked iframe is decided by ParentRedirectGate once the
      // merchant attributes are known, using the URL updated below
      dispatch({
        type: GlobalStateActionType.SET_TOKEN_ID,
        payload: { tokenId },
      });
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tokenId", tokenId);
        if (Boolean(checkoutId)) {
          next.set("checkoutId", checkoutId);
        }
        if (fromCartToCheckout) {
          next.set("referrer", "cart");
        }
        if(Boolean(auth)){
          next.set("auth",auth)
        }
        if(Boolean(test_tag)){
          next.set("test_tag",test_tag)
        }
        next.delete("prewarm");
        return next;
      });
    }
  };

  // For faster loading we take merchant data from parent
  useEffect(() => {
    window.addEventListener("message", handleParentData);

    return () => {
      window.removeEventListener("message", handleParentData);
    };
  }, []);

  const setTokenId = (data: { tokenId: string }) => {
    dispatch({
      type: GlobalStateActionType.SET_TOKEN_ID,
      payload: {
        tokenId: data.tokenId,
      },
    });
  };

  const setSegmentId = (data: { segmentId: string }) => {
    dispatch({
      type: GlobalStateActionType.SET_SEGMENT_ID,
      payload: { segmentId: data.segmentId },
    });
  };

  return (
    <TokenContext.Provider
      value={{
        state: state,
        actions: {
          setTokenId,
          setSegmentId,
        },
      }}>
      {children}
    </TokenContext.Provider>
  );
};
